// Logo 图片资源
import logoZh from '../assets/images/logo.webp';
import logoEn from '../assets/images/logo-en.webp';
import logoCp from '../assets/images/logo-cp.webp';
import logoSaros from '../assets/images/logo-saros.webp';

// Favicon 图片资源
import faviconUxlab from '../assets/images/favicon.ico';
import faviconCheckpoint from '../assets/images/favicon2.ico';
import faviconSaros from '../assets/images/favicon-saros.ico';

// 导入类型和配置
import {
  ENV_CONFIG_MAP,
  DEFAULT_ENV,
} from './configs';
import { confidentialTextOverrides } from './configs/confidential-overrides';
import {
  MODE_OPTIONS,
  RESOLUTION_OPTIONS,
  ENV_OPTIONS,
} from './types';
import type {
  EnvType,
  LogoType,
  IEnvConfig,
  ICOSConfig,
  IGroupConfig,
  IUIConfig,
  IFeatureFlags,
  ITextConfig,
  IDesktopEnvConfig,
  IVersionOption,
  IModeOption,
  IResolutionOption,
  IEnvOption,
  DesktopEnv,
  Mode,
  Resolution,
  IDesktopConfig,
  ISurveyConfig,
} from './types';

// 导出类型
export type {
  EnvType,
  LogoType,
  IEnvConfig,
  ICOSConfig,
  IGroupConfig,
  IUIConfig,
  IFeatureFlags,
  ITextConfig,
  IDesktopEnvConfig,
  IVersionOption,
  IModeOption,
  IResolutionOption,
  IEnvOption,
  DesktopEnv,
  Mode,
  Resolution,
  IDesktopConfig,
};

// 导出选项常量
export { MODE_OPTIONS, RESOLUTION_OPTIONS, ENV_OPTIONS };

const isDevMode = import.meta.env.MODE === 'development';
const allowMockEnv = isDevMode || import.meta.env.MODE === 'staging';

const asDevMockEnv = (raw: string | null): EnvType | '' => {
  const value = raw || '';
  return value in ENV_CONFIG_MAP ? value as EnvType : '';
};

// 本地 / 测试环境认 localStorage.mockEnv；正式环境忽略，避免控制台改 storage 绕过阉割。
const storedMockEnv = ((): EnvType | '' => {
  if (!allowMockEnv) return '';
  try {
    return asDevMockEnv(localStorage.getItem('mockEnv'));
  } catch {
    return '';
  }
})();

// 脱敏判定：主信号源是 desktop preload 通过 contextBridge 注入的
// window.__uxlabBrand__.redacted（desktop 客户端内嵌 web 页面用）。
//
// 兜底信号源：URL query `brand=confidential`。用于 desktop 内复制素材链接
// 到浏览器打开（receiver /material 页面）的场景——浏览器没有 desktop preload
// 注入，必须靠 URL 透传脱敏状态。capturer 在 isBrandRedacted() 为 true 时
// 构造 URL 才追加这个 query，正常浏览器访问不会自带，仍走正式版品牌。
export const isBrandRedacted = (): boolean => {
  try {
    if (window.__uxlabBrand__?.redacted === true) return true;
    const params = new URLSearchParams(window.location.search);
    return params.get('brand') === 'confidential';
  } catch {
    return false;
  }
};

// 本地 / 测试环境认 ?mockEnv=；正式环境忽略，避免改 query 绕过阉割。
const readDevQueryMockEnv = (): EnvType | '' => {
  if (!allowMockEnv) return '';
  try {
    return asDevMockEnv(new URLSearchParams(window.location.search).get('mockEnv'));
  } catch {
    return '';
  }
};

// 获取当前环境
export const getEnvType = (): EnvType => {
  const { origin } = window.location;
  const queryMockEnv = readDevQueryMockEnv();

  if (queryMockEnv) return queryMockEnv;
  if (storedMockEnv) return storedMockEnv;
  if (origin.includes('sarosgame')) return 'saros';
  if (origin.includes('uxlab.imur') || isDevMode) return 'uxlab';
  return 'checkpoint';
};

// 本地 / 测试环境观看链接透传 mockEnv；正式环境原样返回。已带 mockEnv 的 URL 不再追加。
export const appendDevMockEnv = (url: string): string => {
  if (!allowMockEnv) return url;
  let mock: EnvType | '' = '';
  try {
    mock = asDevMockEnv(localStorage.getItem('mockEnv'));
  } catch {
    mock = '';
  }
  if (!mock || /[?&]mockEnv=/.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}mockEnv=${encodeURIComponent(mock)}`;
};

export const buildReceiverWatchUrl = (origin: string, uid: string): string => (
  appendDevMockEnv(`${origin}/receiver/?uid=${uid}`)
);

// 获取域名
export const getDomain = (): string => getDesktopConfig().namespace;

// 根据环境和语言获取 Logo
export const getLogo = (language?: string): string => {
  const envType = getEnvType();
  if (envType === 'checkpoint') {
    return logoCp;
  }
  if (envType === 'saros') {
    return logoSaros;
  }
  return language === 'en' ? logoEn : logoZh;
};

// 根据环境获取 Favicon
export const getFavicon = (): string => {
  const envType = getEnvType();
  if (envType === 'checkpoint') {
    return faviconCheckpoint;
  }
  if (envType === 'saros') {
    return faviconSaros;
  }
  return faviconUxlab;
};

// 获取完整环境配置
export const getEnvConfig = (): IEnvConfig => {
  const envType = getEnvType();
  return ENV_CONFIG_MAP[envType] || ENV_CONFIG_MAP[DEFAULT_ENV];
};

// 兼容旧 API
export const isUXLabEnv = () => getEnvType() === 'uxlab';

// 便捷访问函数
export const getCOSConfig = (): ICOSConfig => getEnvConfig().cos;
export const getGroupConfig = (): IGroupConfig => getEnvConfig().group;
export const getUIConfig = (): IUIConfig => getEnvConfig().ui;
export const getFeatureFlags = (): IFeatureFlags => getEnvConfig().features;

// 关翻译栏时不恢复 localStorage 里残留的目标语种，避免继续拉译文。
export const readCaptionTargetLang = (): string => {
  if (!getFeatureFlags().showCaptionTranslation) return '';
  try {
    return localStorage.getItem('caption_target_lang') ?? '';
  } catch {
    return '';
  }
};

export const getTextConfig = (): ITextConfig => {
  const baseText = getEnvConfig().text;
  if (isBrandRedacted()) {
    return { ...baseText, ...confidentialTextOverrides };
  }
  return baseText;
};
// 获取桌面端配置
export const getDesktopConfig = (): IDesktopEnvConfig => getEnvConfig().desktop;
export const getSurveyConfig = (): ISurveyConfig => getEnvConfig().survey;

// 导出启动初始化相关
export {
  bootstrap,
  setupFavicon,
  initSentry,
  initBeacon,
  initAegis,
} from './bootstrap';
export type { AppType, BootstrapOptions } from './bootstrap';
